import React, { useEffect, useState, useRef } from "react";
import { Search, X, Clock } from "lucide-react";

const MAX_RECENT = 5;

function loadRecent(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, MAX_RECENT) : [];
  } catch {
    return [];
  }
}

function saveRecent(key, list) {
  try {
    localStorage.setItem(key, JSON.stringify(list.slice(0, MAX_RECENT)));
  } catch {
    // ignore
  }
}

/**
 * Input de busca com lista de buscas recentes persistidas em localStorage.
 *
 * Props:
 *  - value, onChange, placeholder, testId
 *  - storageKey: chave única no localStorage (ex: "cm:recent:clients")
 */
export function SearchWithRecent({
  value,
  onChange,
  placeholder = "Buscar…",
  testId = "search-with-recent",
  storageKey,
  className = "",
}) {
  const [recent, setRecent] = useState(() =>
    storageKey ? loadRecent(storageKey) : [],
  );
  const [focused, setFocused] = useState(false);
  const blurTimer = useRef(null);

  useEffect(() => {
    if (!storageKey) return;
    setRecent(loadRecent(storageKey));
  }, [storageKey]);

  function commit(term) {
    const trimmed = (term || "").trim();
    if (!trimmed || !storageKey) return;
    const next = [trimmed, ...recent.filter((r) => r !== trimmed)].slice(0, MAX_RECENT);
    setRecent(next);
    saveRecent(storageKey, next);
  }

  function pickRecent(term) {
    onChange(term);
    commit(term);
    setFocused(false);
  }

  function removeRecent(term) {
    const next = recent.filter((r) => r !== term);
    setRecent(next);
    if (storageKey) saveRecent(storageKey, next);
  }

  function clearAll() {
    setRecent([]);
    if (storageKey) saveRecent(storageKey, []);
  }

  const showRecent = focused && !value && recent.length > 0;

  return (
    <div className={`relative ${className}`}>
      <Search
        size={16}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
      />
      <input
        data-testid={testId}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          // delay para permitir clique na sugestão
          blurTimer.current = setTimeout(() => setFocused(false), 150);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit(value);
          if (e.key === "Escape") onChange("");
        }}
        placeholder={placeholder}
        className="w-full h-11 pl-10 pr-10 rounded-xl border border-slate-200 bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all text-sm"
      />
      {value && (
        <button
          type="button"
          data-testid={`${testId}-clear`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onChange("")}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
          aria-label="Limpar busca"
        >
          <X size={16} />
        </button>
      )}
      {showRecent && (
        <div
          data-testid={`${testId}-recent-list`}
          className="absolute left-0 right-0 top-full mt-1 z-30 rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden"
        >
          <div className="flex items-center justify-between px-3 py-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold bg-slate-50 border-b border-slate-100">
            <span className="inline-flex items-center gap-1.5">
              <Clock size={11} /> Buscas recentes
            </span>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={clearAll}
              data-testid={`${testId}-recent-clear-all`}
              className="text-[10px] text-slate-500 hover:text-slate-900 normal-case tracking-normal"
            >
              Limpar tudo
            </button>
          </div>
          <ul>
            {recent.map((term) => (
              <li
                key={term}
                className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm text-slate-700"
                onMouseDown={(e) => {
                  e.preventDefault();
                  pickRecent(term);
                }}
                data-testid={`${testId}-recent-item`}
              >
                <span className="truncate flex-1">{term}</span>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    removeRecent(term);
                  }}
                  className="text-slate-300 hover:text-slate-700 ml-2"
                  aria-label={`Remover "${term}"`}
                >
                  <X size={13} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
